// src/components/ProtectedRoute.js
import React from 'react';
// eslint-disable-next-line no-unused-vars
import { Navigate } from 'react-router-dom';
import { usePermissions } from '../context/PermissionsContext';
import { Box, Paper, Typography, Button, alpha } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import HomeIcon from '@mui/icons-material/Home';

const ProtectedRoute = ({ 
  children, 
  module, 
  requiredPermission = 'view',
  adminOnly = false,
  strictAdminOnly = false,
  redirectTo = '/dashboard'
}) => {
  const { hasPermission, currentRole } = usePermissions();

  // Verify strict access for Admin only
  if (strictAdminOnly && currentRole !== 'admin') {
    return <AccessDenied redirectTo={redirectTo} />;
  }

  // Verify access for Admin and Manager
  if (adminOnly && currentRole !== 'admin' && currentRole !== 'manager') {
    return <AccessDenied redirectTo={redirectTo} />;
  }

  // Verify specific module permission
  if (module && requiredPermission) {
    if (!hasPermission(module, requiredPermission)) {
      return <AccessDenied redirectTo={redirectTo} />;
    }
  }

  // If all validations pass, render the component
  return <>{children}</>;
};

// Access Denied Component
const AccessDenied = ({ redirectTo }) => {
  const { currentRole, roles } = usePermissions();
  // Attempt to get a display name, fallback to role code
  const roleName = roles?.[currentRole]?.name || currentRole;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 64px)',
        p: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 500,
          width: '100%',
          p: 4,
          textAlign: 'center',
          border: '2px solid',
          borderColor: 'error.main',
          bgcolor: (theme) => alpha(theme.palette.error.main, 0.05),
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            p: 2,
            borderRadius: '50%',
            bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
            mb: 3,
          }}
        >
          <LockIcon sx={{ fontSize: 64, color: 'error.main' }} />
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'error.main' }}>
          Access Restricted
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          You do not have permission to access this page.
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Your current role: <strong>{roleName}</strong>
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={() => window.location.href = redirectTo}
            sx={{ minWidth: 150 }}
          >
            Go to Dashboard
          </Button>
          <Button
            variant="outlined"
            onClick={() => window.history.back()}
            sx={{ minWidth: 150 }}
          >
            Go Back
          </Button>
        </Box>

        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            If you believe this is an error, please contact the system administrator.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default ProtectedRoute;